import React, { useEffect } from 'react';
import { t } from '../../utils/i18n';

/**
 * Dark-theme confirm or alert dialog (replaces window.confirm / window.alert).
 */
export default function AppDialog({
  open,
  title,
  message,
  children,
  mode = 'confirm',
  variant = 'default',
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  loading = false,
  confirmDisabled = false,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) onCancel?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const confirmBtnClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-500 text-white'
      : variant === 'success'
        ? 'bg-green-600 hover:bg-green-500 text-white'
        : 'bg-primary hover:bg-primary/90 text-black';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-dialog-title"
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="bg-dark-card border border-dark-border rounded-xl shadow-xl max-w-md w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <h2 id="app-dialog-title" className="text-lg font-semibold text-dark-text">
            {title}
          </h2>
        ) : null}
        <p className="text-sm text-dark-text-secondary whitespace-pre-wrap leading-relaxed">{message}</p>
        {children}
        <div className="flex justify-end gap-2 pt-1">
          {mode === 'confirm' ? (
            <>
              <button
                type="button"
                disabled={loading}
                onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm border border-dark-border text-dark-text-secondary hover:text-dark-text hover:border-primary/40 disabled:opacity-50"
              >
                {cancelLabel ?? t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={loading || confirmDisabled}
                onClick={onConfirm}
                className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${confirmBtnClass}`}
              >
                {loading ? t('user.ban.processing') : confirmLabel ?? t('common.confirm')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${confirmBtnClass}`}
            >
              {confirmLabel ?? t('common.ok')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
